// Чистая логика GitHub OAuth2 (server-side), вынесенная из Login.vue, чтобы её
// можно было покрыть тестами без монтирования компонента/DOM/axios.
// CommonJS — чтобы модуль без транспиляции запускался и под webpack (импорт
// именованных экспортов через import { ... } from), и напрямую в node --test.

function generateGithubState(getRandomValues) {
    const bytes = new Uint8Array(24);
    (getRandomValues || ((arr) => window.crypto.getRandomValues(arr)))(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function buildGithubAuthorizeUrl({ clientId, redirectUri, scope, state }) {
    const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        scope,
        state,
    });
    return 'https://github.com/login/oauth/authorize?' + params.toString();
}

function parseGithubCallbackParams(search) {
    const query = new URLSearchParams(search);
    return {
        code: query.get('code'),
        state: query.get('state'),
        error: query.get('error'),
        errorDescription: query.get('error_description'),
    };
}

// GitHub всегда возвращает обратно параметр state (RFC 6749 4.1.2/4.1.2.1), в т.ч. и
// в ветке отказа/ошибки — поэтому его отсутствие уже само по себе повод не считать
// query-параметры страницы колбэком GitHub (могут принадлежать другому сценарию).
function isGithubCallback({ code, state, error }) {
    return !!(state && (code || error));
}

function isGithubStateValid(receivedState, savedState) {
    return !!savedState && savedState === receivedState;
}

// Убирает только OAuth-параметры (code/state/error/error_description) из query,
// остальную часть URL (прочие query-параметры, hash) сохраняет как есть.
function stripGithubOauthParams(href) {
    const url = new URL(href);
    ['code', 'state', 'error', 'error_description'].forEach((key) => url.searchParams.delete(key));
    return url.pathname + url.search + url.hash;
}

// Оркестрация обработки возврата с GitHub, вынесенная из Login.vue как чистая (кроме
// инжектируемых side-effect'ов) async-функция — чтобы её можно было тестировать без
// монтирования компонента. Побочные эффекты (sessionStorage, URL, axios, стор, i18n)
// приходят через параметры.
//
// Правило (см. review issue #15): state проверяется ДЛЯ ЛЮБОЙ ветки колбэка, включая
// отказ/ошибку от GitHub — иначе произвольный ?error=...&error_description=...&state=...
// с несовпадающим state показывал бы зрителю чужой текст без проверки, что колбэк
// действительно относится к запущенному этой вкладкой flow.
async function runGithubCallback({
    search,
    readSavedState,
    clearSavedState,
    stripUrl,
    post,
    onLogin,
    onError,
    setBusy,
    genericErrorMessage,
}) {
    const { code, state, error, errorDescription } = parseGithubCallbackParams(search);
    if (!isGithubCallback({ code, state, error })) return 'ignored';

    const saved = readSavedState();
    if (!saved) return 'ignored';

    clearSavedState();
    stripUrl();

    if (!isGithubStateValid(state, saved)) {
        onError(genericErrorMessage);
        return 'invalid-state';
    }
    if (error) {
        onError(errorDescription || genericErrorMessage);
        return 'oauth-error';
    }

    setBusy(true);
    onError('');
    try {
        const res = await post(code);
        onLogin(res);
        return 'success';
    } finally {
        setBusy(false);
    }
}

module.exports = {
    generateGithubState,
    buildGithubAuthorizeUrl,
    parseGithubCallbackParams,
    isGithubCallback,
    isGithubStateValid,
    stripGithubOauthParams,
    runGithubCallback,
};
